/**
 * Threia Totem Item entity — a mystical collectible that appears on the maze floor.
 *
 * When collected, emits a THREIA_TOTEM_COLLECTED event so the HUD can display
 * the totem indicator. Holding the totem guarantees that Threia's Blessing will
 * succeed and also guarantees a life restoration.
 *
 * Visual: uses the TEX_THREIA_TOTEM procedural texture, with a gentle float,
 * subtle rotation, pulsing glow halo, and periodic divine glint.
 *
 * @extends Phaser.GameObjects.Sprite
 */

import Phaser from 'phaser';
import { TEX_THREIA_TOTEM, TEX_GLOW } from '../utils/constants.js';
import {
  TILE_SIZE,
  AUDIO_THREIA_TOTEM_COLLECT,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class ThreiaTotemItem extends Phaser.GameObjects.Sprite {
  /**
   * @param {Phaser.Scene} scene - The scene this totem belongs to.
   * @param {number} col - Grid column (expanded grid).
   * @param {number} row - Grid row (expanded grid).
   */
  constructor(scene, col, row) {
    const x = col * TILE_SIZE + TILE_SIZE / 2;
    const y = row * TILE_SIZE + TILE_SIZE / 2;

    super(scene, x, y, TEX_THREIA_TOTEM);

    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {number} Grid column in the expanded grid. */
    this.gridCol = col;

    /** @type {number} Grid row in the expanded grid. */
    this.gridRow = row;

    scene.add.existing(this);
    this.setDepth(5);
    this.setScale(0);

    // --- Pop-in animation ---
    scene.tweens.add({
      targets: this,
      scale: 1.3,
      duration: 350,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.scene) return;
        scene.tweens.add({
          targets: this,
          scale: 1,
          duration: 180,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // --- Gentle float (up/down) ---
    this._floatTween = scene.tweens.add({
      targets: this,
      y: y - 2.5,
      duration: 1400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // --- Slow mystical rotation ---
    this._rotateTween = scene.tweens.add({
      targets: this,
      angle: { from: -5, to: 5 },
      duration: 2400,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    // --- Glow halo behind the totem ---
    this._glow = scene.add.graphics();
    this._glow.setDepth(4);
    this._glowAlpha = 0.22;
    this._drawGlow();

    this._glowTween = scene.tweens.add({
      targets: this,
      _glowAlpha: 0.06,
      duration: 1800,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        this._drawGlow();
      },
    });

    // --- Periodic divine glint ---
    this._scheduleGlint();
  }

  /**
   * Draw the soft glow halo behind the totem.
   * @private
   */
  _drawGlow() {
    if (!this._glow || !this.scene) return;
    this._glow.clear();
    this._glow.fillStyle(0xffffff, this._glowAlpha);
    this._glow.fillCircle(this.x, this.y, 13);
  }

  /**
   * Schedule the next random glint flash.
   * @private
   */
  _scheduleGlint() {
    if (!this.scene) return;
    const delay = Phaser.Math.Between(3500, 8000);
    this._glintTimer = this.scene.time.delayedCall(delay, () => {
      this._playGlint();
    });
  }

  /**
   * Flash a brief white-gold divine glint on the totem.
   * @private
   */
  _playGlint() {
    if (!this.scene || !this.active) return;

    // Quick alpha flash
    this.scene.tweens.add({
      targets: this,
      alpha: { from: 1, to: 0.35 },
      duration: 250,
      yoyo: true,
      ease: 'Sine.easeInOut',
    });

    // Particle burst — upward divine sparkles
    const emitter = this.scene.add.particles(this.x, this.y, TEX_GLOW, {
      lifespan: 500,
      speed: { min: 10, max: 35 },
      angle: { min: 240, max: 300 },
      scale: { start: 0.45, end: 0 },
      alpha: { start: 0.7, end: 0 },
      frequency: -1,
    });
    emitter.setDepth(6);
    emitter.explode(3);
    this.scene.time.delayedCall(600, () => {
      if (emitter) emitter.destroy();
    });

    this._scheduleGlint();
  }

  /**
   * Collect the totem. Plays sound and a collection animation.
   * The caller is responsible for emitting the THREIA_TOTEM_COLLECTED event.
   */
  collect() {
    if (!this.active) return;
    this.active = false;

    // Cancel timers
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }

    if (!this.scene) return;

    // Play collection sound
    try {
      this.scene.sound.play(AUDIO_THREIA_TOTEM_COLLECT, { volume: getSfxVol(0.6) });
    } catch (_) { /* audio fallback */ }

    // Show collect label
    this._showCollectLabel();

    // Animate collection: divine ascent + fade
    this.scene.tweens.add({
      targets: this,
      scale: 2.8,
      alpha: 0,
      y: this.y - 15,
      duration: 450,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  /**
   * Show a floating "THREIA TOTEM" label above the collection point.
   * @private
   */
  _showCollectLabel() {
    if (!this.scene) return;

    const label = this.scene.add.text(this.x, this.y - 10, 'THREIA TOTEM', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '9px',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 2,
    })
      .setOrigin(0.5)
      .setDepth(150)
      .setScale(0.5);

    this.scene.tweens.add({
      targets: label,
      scale: 2.0,
      duration: 220,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!label.scene) return;
        label.scene.tweens.add({
          targets: label,
          y: label.y - 30,
          alpha: 0,
          duration: 650,
          delay: 350,
          ease: 'Quad.easeOut',
          onComplete: () => {
            label.destroy();
          },
        });
      },
    });
  }

  /**
   * Clean up resources.
   * @param {boolean} [fromScene]
   */
  destroy(fromScene) {
    if (this._glintTimer) {
      this._glintTimer.remove(false);
      this._glintTimer = null;
    }
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    if (this._rotateTween) {
      this._rotateTween.stop();
      this._rotateTween = null;
    }
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glow) {
      this._glow.destroy();
      this._glow = null;
    }
    super.destroy(fromScene);
  }
}
